import { isNonNullableObject } from "@knyt/artisan";
import { DEFAULT_BASE_FONT_SIZE_PX } from "./constants.js";
import { isCSSPercentage } from "./CSSPercentage.js";
/**
 * Represents a CSS length value.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/length | MDN CSS Length}
 */
export class CSSLength {
    static from(value, baseFontSizePx = DEFAULT_BASE_FONT_SIZE_PX) {
        if (value === undefined) {
            return undefined;
        }
        if (typeof value === "number") {
            return new CSSLength(value, baseFontSizePx);
        }
        if (isCSSLength(value)) {
            return value;
        }
        if (value.endsWith(CSSLength.Unit.Pixels)) {
            return new CSSLength(parseFloat(value), baseFontSizePx);
        }
        if (value.endsWith(CSSLength.Unit.Rem)) {
            const pixelValue = parseFloat(value) * baseFontSizePx;
            return new CSSLength(pixelValue, baseFontSizePx);
        }
        throw new TypeError(`CSSLength: Unrecognized value: ${value}`);
    }
    /**
     * The value of the length represented in pixels.
     */
    #pixelValue;
    /**
     * Default base font size in pixels.
     * @defaultValue 16
     * @remarks This value is used to convert between pixels and rems.
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/font-size | MDN font-size}
     */
    #baseFontSizePx;
    constructor(pixelValue, baseFontSizePx = DEFAULT_BASE_FONT_SIZE_PX) {
        this.#pixelValue = pixelValue;
        this.#baseFontSizePx = baseFontSizePx;
    }
    /**
     * Convert the length into a value using pixels.
     */
    toPixelValue() {
        return this.#pixelValue;
    }
    /**
     * Convert the length into a value using the base font size.
     */
    toRemValue() {
        return this.#pixelValue / this.#baseFontSizePx;
    }
    /**
     * Convert the length into a string with a "px" unit.
     */
    px() {
        return `${this.toPixelValue()}${CSSLength.Unit.Pixels}`;
    }
    /**
     * Convert the length into a string with a "rem" unit.
     */
    rem() {
        return `${this.toRemValue()}${CSSLength.Unit.Rem}`;
    }
    /**
     * Convert the length into a value using the given unit.
     */
    toValue(unit) {
        switch (unit) {
            case CSSLength.Unit.Pixels:
                return this.toPixelValue();
            case CSSLength.Unit.Rem:
                return this.toRemValue();
            default:
                throw new Error(`CSSLength: Unrecognized unit: ${unit}`);
        }
    }
    /**
     * Convert the length into a string with the given unit.
     */
    toLengthString(unit) {
        switch (unit) {
            case CSSLength.Unit.Pixels:
                return this.px();
            case CSSLength.Unit.Rem:
                return this.rem();
            default:
                throw new Error(`CSSLength: Unrecognized unit: ${unit}`);
        }
    }
    /**
     * Add this length to another length.
     */
    plus(other) {
        return this.#math(other, (a, b) => a + b);
    }
    /**
     * Subtract another length from this length.
     */
    minus(other) {
        return this.#math(other, (a, b) => a - b);
    }
    /**
     * Multiply this length by another length or a number.
     */
    times(other) {
        return this.#math(other, (a, b) => a * b);
    }
    /**
     * Get the maximum value between this length and another length.
     */
    max(other) {
        return this.#math(other, (a, b) => Math.max(a, b));
    }
    /**
     * Get the minimum value between this length and another length.
     */
    min(other) {
        return this.#math(other, (a, b) => Math.min(a, b));
    }
    /**
     * Divide this length by another length or a number.
     *
     * @remarks
     *
     * The result of division is always rounded,
     * because sub-pixel values are not allowed.
     */
    dividedBy(other, rounding = "floor") {
        return this.#math(other, (pixelValueA, pixelValueB) => {
            return Math[rounding](pixelValueA / pixelValueB);
        });
    }
    #round(rounding) {
        return new CSSLength(Math[rounding](this.toPixelValue()), this.#baseFontSizePx);
    }
    round() {
        return this.#round("round");
    }
    floor() {
        return this.#round("floor");
    }
    ceil() {
        return this.#round("ceil");
    }
    /**
     * Rounds up to the nearest even number.
     *
     * @alpha
     */
    even() {
        return new CSSLength(Math.round(this.toPixelValue() / 2) * 2, this.#baseFontSizePx);
    }
    /**
     * Validates the pixel value of the length.
     *
     * @returns An array of error messages.
     */
    validate({ min, max } = {}) {
        const pixelValue = this.#pixelValue;
        const errors = [];
        if (isNaN(pixelValue)) {
            errors.push("Pixel value is not a number.");
        }
        if (!isFinite(pixelValue)) {
            errors.push("Pixel value is not finite.");
        }
        if (min !== undefined) {
            const minLength = CSSLength.from(min);
            if (!minLength.isValid()) {
                errors.push(`Min length is invalid: ${min}`);
            }
            else if (pixelValue < minLength.toPixelValue()) {
                errors.push(`Pixel value of ${pixelValue} is less than ${min}.`);
            }
        }
        if (max !== undefined) {
            const maxLength = CSSLength.from(max);
            if (!maxLength.isValid()) {
                errors.push(`Max length is invalid: ${max}`);
            }
            else if (pixelValue > maxLength.toPixelValue()) {
                errors.push(`Pixel value of ${pixelValue} is greater than ${max}.`);
            }
        }
        return errors;
    }
    isValid(config) {
        return this.validate(config).length === 0;
    }
    assertValidity(config) {
        const errors = this.validate(config);
        if (errors.length > 0) {
            throw new Error(`CSSLength: ${errors.join(" ")}`);
        }
    }
    /**
     * Log validation errors to the console,
     * and returns whether the length is valid.
     */
    checkValidity(config) {
        const errors = this.validate(config);
        if (errors.length > 0) {
            console.error(`CSSLength: ${errors.join(" ")}`);
            return false;
        }
        return true;
    }
    /**
     * Enables automatic type coercion when using arithmetic operators.
     *
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/valueOf | MDN Primitive coercion}
     */
    valueOf() {
        return this.toPixelValue();
    }
    /**
     * Enables automatic type coercion into strings.
     *
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/toString | MDN Primitive coercion}
     */
    toString() {
        return this.px();
    }
    toCSSString() {
        return this.px();
    }
    /**
     * Enables automatic type coercion into numbers or strings.
     *
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Symbol/toPrimitive | MDN Symbol.toPrimitive}
     */
    [Symbol.toPrimitive](hint) {
        switch (hint) {
            case "number":
                return this.toPixelValue();
            case "string":
                return this.px();
            default:
                return this.toPixelValue();
        }
    }
    /**
     * Perform a mathematical operation on this length and another length.
     */
    #math(other, operation) {
        const otherValue = isCSSPercentage(other)
            ? other.toRatioDecimalValue()
            : CSSLength.from(other).toPixelValue();
        return new CSSLength(operation(this.toPixelValue(), otherValue), this.#baseFontSizePx);
    }
    get __KnytCSSLength() {
        return true;
    }
}
(function (CSSLength) {
    /**
     * Recognized CSS length units.
     *
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/length | MDN CSS length}
     */
    let Unit;
    (function (Unit) {
        Unit["Pixels"] = "px";
        Unit["Rem"] = "rem";
    })(Unit = CSSLength.Unit || (CSSLength.Unit = {}));
})(CSSLength || (CSSLength = {}));
export function isCSSLength(value) {
    return (isNonNullableObject(value) &&
        "__KnytCSSLength" in value &&
        value.__KnytCSSLength === true);
}
